import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { useAllProjects } from '@/lib/hooks/useProjects'
import { volumeApi, analysisApi } from '@/lib/api'
import { BarChart3, Hash, Globe, TrendingUp, Users, Newspaper, Layers, Loader2, Radio } from 'lucide-react'

export function VolumePage() {
  const { t } = useTranslation()
  const [timePeriod, setTimePeriod] = useState(7)
  const { projects, loading: projLoading } = useAllProjects()
  const [projectId, setProjectId] = useState('')
  const [loading, setLoading] = useState(false)
  const [volumeData, setVolumeData] = useState<any>(null)
  const [emotionData, setEmotionData] = useState<any>(null)
  const [sourceData, setSourceData] = useState<any[]>([])
  const [wordCloud, setWordCloud] = useState<any[]>([])
  const [hotEvents, setHotEvents] = useState<any[]>([])
  const [clusterData, setClusterData] = useState<any[]>([])
  const [exposureData, setExposureData] = useState<any[]>([])
  const [trendData, setTrendData] = useState<any[]>([])

  // Auto-select first project when loaded
  useEffect(() => {
    if (!projectId && projects.length > 0) {
      setProjectId(projects[0].project_id)
    }
  }, [projects, projectId])

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [vol, emo, source, words, events, cluster, exposure, trend] = await Promise.allSettled([
        volumeApi.getProject(projectId, timePeriod),
        analysisApi.emotionalProportion(projectId, timePeriod),
        analysisApi.dataSourceDistribution(projectId, timePeriod),
        analysisApi.highwordCloud(projectId, timePeriod),
        analysisApi.hotEventRanking(projectId, timePeriod),
        (analysisApi as any).topicCluster?.(projectId, timePeriod) || Promise.resolve({ data: [] }),
        (analysisApi as any).keywordExposureRank?.(projectId, timePeriod) || Promise.resolve({ data: [] }),
        analysisApi.keywordEmotionTrend(projectId, timePeriod),
      ])

      const unwrap = (r: any) => r?.status === 'fulfilled' ? ((r.value as any)?.data ?? r.value) : null
      if (vol.status === 'fulfilled') setVolumeData(unwrap(vol))
      if (emo.status === 'fulfilled') setEmotionData(unwrap(emo))
      if (source.status === 'fulfilled') {
        const sData = unwrap(source) || []
        setSourceData(Array.isArray(sData) ? sData : [])
      }
      if (words.status === 'fulfilled') {
        const wData = unwrap(words) || []
        setWordCloud(Array.isArray(wData) ? wData : [])
      }
      if (events.status === 'fulfilled') {
        const eData = unwrap(events) || []
        setHotEvents(Array.isArray(eData) ? eData : [])
      }
      if (cluster.status === 'fulfilled') {
        const cData = unwrap(cluster) || []
        setClusterData(Array.isArray(cData) ? cData : [])
      }
      if (exposure.status === 'fulfilled') {
        const xData = unwrap(exposure) || []
        setExposureData(Array.isArray(xData) ? xData : [])
      }
      if (trend.status === 'fulfilled') {
        const tData = unwrap(trend) || []
        setTrendData(Array.isArray(tData) ? tData : [])
      }
    } catch (err) {
      console.error('Volume fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }, [projectId, timePeriod])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const tabs = [
    { value: 'emotion', icon: BarChart3, label: t('volume.keywordEmotionStatistical') },
    { value: 'source', icon: Globe, label: t('volume.keywordSourceDistribution') },
    { value: 'news', icon: Newspaper, label: t('volume.keywordNewsRank') },
    { value: 'cluster', icon: Layers, label: t('volume.topicClusterAnalysis') },
    { value: 'trend', icon: TrendingUp, label: t('volume.keywordEmotionTrend') },
    { value: 'cloud', icon: Hash, label: t('volume.highwordCloud') },
    { value: 'exposure', icon: TrendingUp, label: t('volume.keywordExposureRank') },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('volume.title')}</h2>
        <div className="flex items-center gap-3">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={projLoading ? t('common.loading') : t('monitor.projectName')} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(timePeriod)} onValueChange={(v) => setTimePeriod(Number(v))}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">{t('monitor.today')}</SelectItem>
              <SelectItem value="4">{t('monitor.days3')}</SelectItem>
              <SelectItem value="5">{t('monitor.days7')}</SelectItem>
              <SelectItem value="6">{t('monitor.days15')}</SelectItem>
              <SelectItem value="7">{t('monitor.days30')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Volume overview cards */}
      {volumeData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">{t('volume.totalVolume') || '总声量'}</p>
              <p className="text-3xl font-bold mt-2">{volumeData.total_volume || '--'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">{t('volume.newsVolume') || '新闻声量'}</p>
              <p className="text-3xl font-bold mt-2">{volumeData.news_volume || '--'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">{t('volume.socialVolume') || '社交声量'}</p>
              <p className="text-3xl font-bold mt-2">{volumeData.social_volume || '--'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="emotion">
        <TabsList className="flex w-full flex-wrap h-auto">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              <tab.icon className="mr-1.5 h-3.5 w-3.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Emotion Statistical */}
        <TabsContent value="emotion">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />{t('volume.keywordEmotionStatistical')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : emotionData ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-around py-8">
                    <EmotionStat label={t('monitor.positive')} value={emotionData.positive || 0} color="text-green-500" bg="bg-green-100" />
                    <EmotionStat label={t('monitor.neutral')} value={emotionData.neutral || 0} color="text-gray-400" bg="bg-gray-100" />
                    <EmotionStat label={t('monitor.negative')} value={emotionData.negative || 0} color="text-red-500" bg="bg-red-100" />
                  </div>
                  <div className="space-y-3">
                    <EmotionBar label={t('monitor.positive')} value={emotionData.positive || 0} color="bg-green-500" />
                    <EmotionBar label={t('monitor.neutral')} value={emotionData.neutral || 0} color="bg-gray-400" />
                    <EmotionBar label={t('monitor.negative')} value={emotionData.negative || 0} color="bg-red-500" />
                  </div>
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Source Distribution */}
        <TabsContent value="source">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" />{t('volume.keywordSourceDistribution')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : sourceData.length > 0 ? (
                <div className="space-y-3">
                  {sourceData.map((s: any, i: number) => {
                    const max = Math.max(...sourceData.map((s: any) => s.count || s.value || 0))
                    const pct = max > 0 ? ((s.count || s.value || 0) / max) * 100 : 0
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2"><Globe className="h-3 w-3 text-muted-foreground" />{s.name || s.source_name}</span>
                          <span className="font-medium">{s.count || s.value}</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
                      </div>
                    )
                  })}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* News Rank */}
        <TabsContent value="news">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Newspaper className="h-5 w-5" />{t('volume.keywordNewsRank')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : hotEvents.length > 0 ? (
                <div className="space-y-2">
                  {hotEvents.slice(0, 10).map((event: any, i: number) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < 3 ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                        <span className="text-sm truncate">{event.event_name || event.name}</span>
                      </div>
                      <Badge variant="secondary">{event.count || event.hot}</Badge>
                    </div>
                  ))}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cluster Analysis */}
        <TabsContent value="cluster">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />{t('volume.topicClusterAnalysis')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : clusterData.length > 0 ? (
                <div className="space-y-3">
                  {clusterData.map((c: any, i: number) => {
                    const max = Math.max(...clusterData.map((c: any) => c.count || 0))
                    const pct = max > 0 ? ((c.count || 0) / max) * 100 : 0
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2"><Layers className="h-3 w-3 text-muted-foreground" />{c.cluster_name || c.name}</span>
                          <span className="font-medium">{c.count} ({c.percentage || 0}%)</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-indigo-500" style={{ width: `${pct}%` }} /></div>
                      </div>
                    )
                  })}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trend */}
        <TabsContent value="trend">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />{t('volume.keywordEmotionTrend')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : trendData.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" />{t('monitor.positive')}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gray-400" />{t('monitor.neutral')}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" />{t('monitor.negative')}</span>
                  </div>
                  {trendData.map((d: any, i: number) => {
                    const max = Math.max(...trendData.map((t: any) => Math.max(t.positive || 0, t.neutral || 0, t.negative || 0))) || 1
                    return (
                      <div key={i} className="space-y-1">
                        <span className="text-xs text-muted-foreground">{d.date}</span>
                        <div className="flex h-6 gap-0.5 overflow-hidden rounded">
                          <div className="h-full bg-green-500 flex items-center justify-center" style={{ width: `${((d.positive || 0) / max) * 100}%` }} title={`正面: ${d.positive || 0}`}>
                            {(d.positive || 0) > max * 0.15 && <span className="text-[10px] text-white">{d.positive}</span>}
                          </div>
                          <div className="h-full bg-gray-400 flex items-center justify-center" style={{ width: `${((d.neutral || 0) / max) * 100}%` }} title={`中性: ${d.neutral || 0}`}>
                            {(d.neutral || 0) > max * 0.15 && <span className="text-[10px] text-white">{d.neutral}</span>}
                          </div>
                          <div className="h-full bg-red-500 flex items-center justify-center" style={{ width: `${((d.negative || 0) / max) * 100}%` }} title={`负面: ${d.negative || 0}`}>
                            {(d.negative || 0) > max * 0.15 && <span className="text-[10px] text-white">{d.negative}</span>}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : volumeData?.trend ? (
                <div className="space-y-2">
                  {volumeData.trend.map((item: any, i: number) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-24">{item.date}</span>
                      <div className="flex-1 h-6 rounded bg-muted overflow-hidden">
                        <div className="h-full bg-primary/60 flex items-center justify-end pr-2" style={{ width: `${Math.min(100, (item.volume / Math.max(...volumeData.trend.map((t: any) => t.volume))) * 100)}%` }}>
                          <span className="text-xs text-white font-medium">{item.volume}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Word Cloud */}
        <TabsContent value="cloud">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Hash className="h-5 w-5" />{t('volume.highwordCloud')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : wordCloud.length > 0 ? (
                <div className="flex flex-wrap gap-2 justify-center py-8">
                  {wordCloud.map((word: any, i: number) => {
                    const size = Math.max(12, Math.min(32, (word.count || word.weight || 10) / 3))
                    return (
                      <span key={i} className="cursor-pointer rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/20 transition-colors" style={{ fontSize: `${size}px` }}>
                        {word.word || word.name}
                      </span>
                    )
                  })}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exposure Rank */}
        <TabsContent value="exposure">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5" />{t('volume.keywordExposureRank')}</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : exposureData.length > 0 ? (
                <div className="space-y-2">
                  {exposureData.map((kw: any, i: number) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < 3 ? 'bg-orange-100 text-orange-600' : 'bg-muted text-muted-foreground'}`}>{kw.rank || i + 1}</span>
                        <span className="text-sm truncate">{kw.keyword || kw.name}</span>
                      </div>
                      <Badge variant="secondary">{kw.exposure || kw.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : <EmptyState />}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function EmotionStat({ label, value, color, bg }: { label: string, value: number, color: string, bg: string }) {
  return (
    <div className="text-center">
      <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${bg}`}>
        <span className={`text-2xl font-bold ${color}`}>{value}%</span>
      </div>
      <p className="mt-2 text-sm font-medium">{label}</p>
    </div>
  )
}

function EmotionBar({ label, value, color }: { label: string, value: number, color: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{value}%</span>
      </div>
      <div className="h-3 rounded-full bg-muted">
        <div className={`h-3 rounded-full ${color}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  )
}

function EmptyState({ hint }: { hint?: string }) {
  return (
    <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-2">
      <p>暂无数据</p>
      {hint && <p className="text-xs opacity-60">{hint}</p>}
    </div>
  )
}
